import { AxiosInstance } from 'axios'
import {
  FavoritePlacePayload,
  GetFavoritePlacesParams,
  Place,
  PlaceShort,
  PurplePlacePayload,
  PurplePlaceUpdatePayload,
  UpdatePlacePayload
} from 'types/places'
import { PLACES_ENDPOINTS } from 'api/ApiRoutes'
import { ID } from 'types/utility'
import { cleanObject } from 'utils/Functions'

class PlacesApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getFavoritePlaces = async ({
    team_token_id,
    teamOnly = false,
    search,
    filter,
    filter_params,
    page = 1,
    limit = 30,
    archived,
    teamEndpoint
  }: GetFavoritePlacesParams) => {
    const sortBy =
      filter === 'recently_added'
        ? 'created_at'
        : filter === 'alphabetical'
          ? 'name'
          : undefined
    const closest = filter === 'closest'

    // Same as `TripsApi.getTrips`: axios encodes the values, and `cleanObject`
    // drops the empty ones so no bare `&` or `search=` goes on the wire.
    const params = cleanObject({
      search,
      type: teamOnly ? 'team' : team_token_id ? 'combined' : undefined,
      team_token_id,
      sort_by: sortBy,
      latitude: closest ? filter_params?.lat : undefined,
      longitude: closest ? filter_params?.lng : undefined,
      archived,
      page,
      per_page: limit,
      paginate: 'by_page'
    })

    if (teamEndpoint && team_token_id) {
      const { data: teamData } = await this.axiosInstance.get<PlaceShort[]>(
        PLACES_ENDPOINTS.TEAM_PLACES(team_token_id),
        { params }
      )
      return teamData
    }

    const { data } = await this.axiosInstance.get<PlaceShort[]>(
      PLACES_ENDPOINTS.BASE,
      { params }
    )
    return data
  }

  getFavoritePlace = async (place_token_id: ID, team_token_id?: ID) => {
    if (team_token_id) {
      const { data: teamData } = await this.axiosInstance.get<PlaceShort>(
        PLACES_ENDPOINTS.TEAM_PLACE(team_token_id, place_token_id)
      )
      return teamData
    }

    const { data } = await this.axiosInstance.get<PlaceShort>(
      PLACES_ENDPOINTS.PLACE(place_token_id)
    )
    return data
  }

  createFavoritePlace = async (
    payload: FavoritePlacePayload,
    team_token_id?: ID
  ) => {
    if (team_token_id) {
      const { data: teamData } = await this.axiosInstance.post(
        PLACES_ENDPOINTS.TEAM_PLACES(team_token_id),
        { place: payload }
      )
      return teamData
    }

    const { data } = await this.axiosInstance.post(
      PLACES_ENDPOINTS.BASE,
      payload
    )
    return data
  }

  // Unversioned route on purpose: the interceptor's base url already ends in
  // the version, so `'v6/places'` would resolve to `…/v6/v6/places`.
  createPurplePlace = async (payload: PurplePlacePayload) => {
    const { data } = await this.axiosInstance.post<Place>(
      PLACES_ENDPOINTS.BASE,
      payload
    )
    return data
  }

  // Not `updateFavoritePlace`: the green methods send the retired
  // `classification` field. One PUT carries `name` and `purpose` together.
  updatePurplePlace = async (
    tokenId: ID,
    payload: PurplePlaceUpdatePayload
  ) => {
    const { data } = await this.axiosInstance.put<Place>(
      PLACES_ENDPOINTS.PLACE(tokenId),
      payload
    )
    return data
  }

  // The 204 has no body, so nothing is read off the response.
  deletePurplePlace = async (tokenId: ID): Promise<void> => {
    await this.axiosInstance.delete(PLACES_ENDPOINTS.PLACE(tokenId))
  }

  updateFavoritePlace = async (
    payload: UpdatePlacePayload,
    team_token_id?: ID
  ) => {
    if (team_token_id) {
      const { data: teamData } = await this.axiosInstance.put(
        PLACES_ENDPOINTS.TEAM_PLACE(team_token_id, payload.token_id),
        { place: payload }
      )
      return teamData
    }
    const { data } = await this.axiosInstance.put(
      PLACES_ENDPOINTS.PLACE(payload.token_id),
      {
        ...payload
      }
    )
    return data
  }

  deleteFavoritePlace = async ({
    team_token_id,
    place_token_id
  }: {
    team_token_id?: ID
    place_token_id: ID
  }) => {
    if (team_token_id) {
      const { data: teamData } = await this.axiosInstance.delete<{
        result: 'success'
      }>(PLACES_ENDPOINTS.TEAM_PLACE(team_token_id, place_token_id))
      return teamData
    }
    const { data } = await this.axiosInstance.delete<{
      result: 'success'
    }>(PLACES_ENDPOINTS.PLACE(place_token_id))
    return data
  }

  activateFavoritePlace = async ({
    team_token_id,
    place_token_id
  }: {
    team_token_id: ID
    place_token_id: ID
  }) => {
    const d = await this.axiosInstance.put<PlaceShort>(
      PLACES_ENDPOINTS.ACTIVATE_TEAM_PLACE(team_token_id, place_token_id)
    )
    return d.data
  }

  updatePlaceClassification = async ({
    place_token_id,
    classification
  }: {
    place_token_id: ID
    classification: PlaceShort['classification']
  }) => {
    const { data } = await this.axiosInstance.patch<PlaceShort>(
      PLACES_ENDPOINTS.PLACE(place_token_id),
      { classification }
    )
    return data
  }

  importFavoritePlaces = async ({
    team_token_id,
    file
  }: {
    team_token_id: ID
    file: File
  }) => {
    const formData = new FormData()
    formData.append('file', file)
    const d = await this.axiosInstance.post<{
      result: 'success'
      token_id: ID
    }>(PLACES_ENDPOINTS.IMPORT_TEAM_PLACES(team_token_id), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return d.data
  }
}

export default PlacesApi
